import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { BonusChip, Button, FormatPrice, InstantChip, PriceChip, Text } from '@reveni-io/reveni-ui'
import { refundMethods as allowedRefundMethods } from '@reveni-io/reveni-ui'

import Summary from '@/components/Summary/Summary'
import AppContext from '@/context/AppContext'
import useCancellationsPortal from '@/hooks/useCancellationsPortal/useCancellationsPortal'
import useSummary from '@/hooks/useSummary/useSummary'

import { getOptions } from '../helpers'
import useRefundMethod from './hooks/useRefundMethod/useRefundMethod'
import { getExtraCreditBonus, getRefundMethodOptions } from './helpers'
import * as Styles from './styles'

const RefundMethodButtons = ({
  handleBankTransfer,
  handleOriginalPaymentMethod,
  handleStoreCredit,
  handleGiftCard,
  refundMethods,
  disabledPreselectedRefundMethod,
  issueRefundAtStatus,
}) => {
  const { t, i18n } = useTranslation()
  const { context } = useContext(AppContext)
  const { setBonuses, setFees } = useSummary()
  const { isCancellationsPortal } = useCancellationsPortal()
  const refundMethodOptions = getOptions({
    options: getRefundMethodOptions,
    t,
    storeName: context.merchantInfo.name,
    refundMethods,
    currency: context.orderAmount.currency,
    language: i18n.language,
    issueRefundAtStatus,
    isCancellationsPortal,
  })
  const { refundMethodSelected, setRefundMethodSelected } = useRefundMethod({
    refundMethods,
    refundMethodOptions,
    disabledPreselectedRefundMethod,
  })

  const setSummaryAmounts = () => {
    const refundMethod = refundMethods.find(rm => rm.name === refundMethodSelected)
    const refundMethodFee = Number(refundMethod?.fee_amount || 0)
    const refundMethodExtraAmount = Number(refundMethod?.extra_amount || 0)

    setFees({
      fees: {
        instant_fee_amount: refundMethodFee,
      },
    })

    const bonuses = getExtraCreditBonus(refundMethodExtraAmount, refundMethodSelected)
    setBonuses(bonuses)
  }

  useEffect(setSummaryAmounts, [
    context.orderAmount.summary_total_amount,
    refundMethodSelected,
    refundMethods,
    setBonuses,
    setFees,
  ])

  const handleSelectRefundOption = () => {
    if (refundMethodSelected === allowedRefundMethods.bankTransfer) {
      handleBankTransfer()
    } else if (refundMethodSelected === allowedRefundMethods.originalPaymentMethod) {
      handleOriginalPaymentMethod()
    } else if (refundMethodSelected === allowedRefundMethods.storeCredit) {
      handleStoreCredit()
    } else {
      handleGiftCard()
    }
  }

  const isSelected = option => option.value === refundMethodSelected

  return (
    <>
      {refundMethodOptions.map((option, index) => (
        <Styles.RefundOption
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.4,
            scale: { type: 'spring', visualDuration: 0.3 * (index + 1), bounce: 0.4 },
          }}
          data-testid={`descriptiveButton${option.value}`}
          onClick={() => setRefundMethodSelected(option.value)}
          key={option.value}
          isSelected={isSelected(option)}
        >
          <Styles.RefundOptionHeader>
            <Styles.RefundIconContainer>{option.icon}</Styles.RefundIconContainer>
            <Styles.RefundOptionTitleContainer>
              <Text type="titleM">{option.label}</Text>
              <Styles.RefundOptionChipsContainer>
                {option.is_instant && <InstantChip />}
                {option.extraCredit && <BonusChip label={`${option.extraCredit} extra`} />}
                {option.feeAmount && (
                  <PriceChip
                    isActive={isSelected(option)}
                    label={
                      <>
                        {t('Coste adicional')}{' '}
                        <FormatPrice currency={context.orderAmount.currency}>{option.feeAmount}</FormatPrice>
                      </>
                    }
                  />
                )}
              </Styles.RefundOptionChipsContainer>
            </Styles.RefundOptionTitleContainer>
          </Styles.RefundOptionHeader>
          <Styles.HelperText type="bodyMRegular">{option.helperText}</Styles.HelperText>
          {!isCancellationsPortal && (
            <Styles.TotalAmountContainer>
              <Text type="bodyMBold">
                {t('Total reembolso')}
                {': '}
                <FormatPrice currency={context.orderAmount.currency}>{option.refundAmount}</FormatPrice>
              </Text>
            </Styles.TotalAmountContainer>
          )}
        </Styles.RefundOption>
      ))}
      <Summary />
      <Button onClick={handleSelectRefundOption} disabled={!refundMethodSelected} fullWidth>
        {t('Siguiente')}
      </Button>
    </>
  )
}

export default RefundMethodButtons
